// src/components/crm/LogFollowUpModal.js
// SEAM 6 VERIFIER: qfup-7d3e9a41-modal
//
// "Log follow-up" capture on the operator quote detail page. Records ONE
// attempt: how you followed up, what happened, an optional note, and an
// optional next follow-up date.
//
// This is a HISTORY, not a field — Stacey Boychuk's point. Logging twice leaves
// two rows on the timeline; it does not overwrite the first.
//
// INTERNAL ONLY: never mount this in a portal view.
//
// Writes go through the logQuoteFollowUp Cloud Function — firestore.rules
// denies client writes to nextFollowUpAt and followUpCount. The page's live
// quote/activity listeners deliver the new row and counts afterwards.
//
// The method/result options come from the org's own vocabularies
// (settings/quoteVocabularies, edited in Settings). They fall back to the
// frozen defaults, so this modal works for an org that has never opened that
// screen.
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { X, PhoneCall } from 'lucide-react';
import { functions } from '../../firebase/config';

const LogFollowUpModal = ({ isOpen, onClose, quote, vocabularies, docWord = 'Estimate' }) => {
    const methods = vocabularies?.followUpMethods || [];
    const results = vocabularies?.followUpResults || [];

    const [method, setMethod] = useState('');
    const [result, setResult] = useState('');
    const [note, setNote] = useState('');
    const [nextFollowUpAt, setNextFollowUpAt] = useState('');
    const [nextFollowUpTime, setNextFollowUpTime] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen || !quote?.id) return null;

    const reset = () => {
        setMethod(''); setResult(''); setNote(''); setNextFollowUpAt(''); setNextFollowUpTime('');
        setError(''); setSaving(false);
    };

    const handleClose = () => { if (!saving) { reset(); onClose(); } };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;                       // single-fire guard
        if (!method || !result) {
            setError('Choose how you followed up and what the result was.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await httpsCallable(functions, 'logQuoteFollowUp')({
                quoteId: quote.id,
                method,
                result,
                note: note.trim() || null,
                // qsnooze-9f4c2e18 — a time is OPTIONAL. With one, send that
                // instant and say so. Without one, keep Seam 6's midday-local
                // proxy so a date-only intention can't slide to the previous
                // day west of UTC — and flag it as date-only, because noon is
                // also a time a real person picks and the stored Timestamp
                // cannot tell the two apart on its own.
                nextFollowUpAt: nextFollowUpAt
                    ? new Date(`${nextFollowUpAt}T${nextFollowUpTime || '12:00'}:00`).toISOString()
                    : null,
                nextFollowUpHasTime: !!(nextFollowUpAt && nextFollowUpTime),
            });
            reset();
            onClose();
        } catch (err) {
            console.error('LogFollowUpModal: log failed', err);
            setError(err?.message || 'Could not log this follow-up. Please try again.');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                        <PhoneCall size={18} className="text-[#5F4EC9]" /> Log follow-up
                    </h3>
                    <button type="button" onClick={handleClose} disabled={saving}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            How did you follow up? <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={method}
                            onChange={(e) => setMethod(e.target.value)}
                            disabled={saving}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white disabled:opacity-60"
                        >
                            <option value="">Choose…</option>
                            {methods.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            What happened? <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={result}
                            onChange={(e) => setResult(e.target.value)}
                            disabled={saving}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white disabled:opacity-60"
                        >
                            <option value="">Choose…</option>
                            {results.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Note <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={saving}
                            rows={3}
                            maxLength={2000}
                            placeholder="Anything worth remembering about this attempt"
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-60"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Next follow-up <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="date"
                                value={nextFollowUpAt}
                                onChange={(e) => setNextFollowUpAt(e.target.value)}
                                disabled={saving}
                                className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-60"
                            />
                            <input
                                type="time"
                                value={nextFollowUpTime}
                                onChange={(e) => setNextFollowUpTime(e.target.value)}
                                disabled={saving || !nextFollowUpAt}
                                aria-label="Time of day (optional)"
                                title={nextFollowUpAt ? 'Time of day (optional)' : 'Pick a date first'}
                                className="w-28 shrink-0 px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-60 disabled:bg-gray-50"
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-400">
                            Leave the time blank for &ldquo;sometime that day&rdquo;.
                        </p>
                    </div>

                    {error && <p className="text-sm text-red-600">{error}</p>}

                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={handleClose} disabled={saving}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#5F4EC9] rounded-lg hover:bg-[#4f40a8] disabled:opacity-50">
                            {saving ? 'Saving…' : 'Log follow-up'}
                        </button>
                    </div>
                    <p className="text-xs text-gray-400 pt-1">
                        Internal: the customer never sees this. It is added to the {docWord.toLowerCase()}&apos;s history.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default LogFollowUpModal;
